import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, observable, makeObservable, computed } from 'mobx'
import { task } from 'mobx-task'
import { Subject } from 'rxjs'
import CouponStore from 'billing/CouponStore'
import { Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import Coupon from 'billing/models/Coupon'
import { CouponStatus } from 'billing/dtos/coupon'
import { debounceTime, distinct } from 'rxjs/operators'

/**
 * View Model for the Apply Coupon as Admin dialog.
 * @extends ModalViewModel
 */
export default class ApplyCouponAsAdminViewModel extends ModalViewModel {
  /**
   * Coupon to add.
   */
  @observable selectedCouponId: Id = undefined

  /**
   * SearchTerm to filter coupons by.
   */
  @observable searchTerm = ''

  /**
   * Trigger coupon search on changes.
   */
  private readonly _triggerSearch = new Subject<string>()

  constructor(
    private job: Job,
    private jobStore: JobStore,
    private couponStore: CouponStore,
    private newAlert: AlertFactory,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)

    this._triggerSearch
      .pipe(debounceTime(200), distinct())
      .subscribe(() => this.scrollProvider.fetch())
  }

  @computed
  get coupons() {
    const redeemedCoupons = this.couponStore
      .couponsForMember(this.job.owner)
      .filter((memberCoupon) => memberCoupon.redeemed)
      .map((memberCoupon) => memberCoupon.coupon.id)

    const availableCoupons = this.couponStore
      .forWorkspace(Id.assert(this.job.workspace.id))
      .filter(
        (coupon) =>
          coupon.couponType.type === 'REGULAR' &&
          (coupon.duration === 'FOREVER' ||
            !redeemedCoupons.includes(coupon.id)),
      )

    if (!this.searchTerm) {
      return availableCoupons
    }

    const searchTerm = this.searchTerm.toLowerCase()
    return availableCoupons.filter(
      (coupon) =>
        coupon.code.toLowerCase().includes(searchTerm) ||
        coupon.friendlyName?.toLowerCase().includes(searchTerm),
    )
  }

  /**
   * Shows the Apply Coupon as Admin Dialog.
   */
  show() {
    this.fetchUserCoupons().then()
    super.show()
  }

  /**
   * Sets couponId to add.
   */
  @action.bound
  setCoupon(coupon: Coupon) {
    this.selectedCouponId = coupon.id
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.selectedCouponId = Id.none

    this.applyCouponAsAdmin.reset()
  }

  @action
  searchCoupon(searchTerm: string) {
    this.searchTerm = searchTerm
    this._triggerSearch.next(searchTerm)
  }

  /**
   * Applies the coupon as Admin.
   */
  applyCouponAsAdmin = task.resolved(async () => {
    const alert = this.newAlert()

    if (!this.selectedCouponId) {
      return
    }

    try {
      await this.jobStore.applyCouponAsAdmin(this.job, this.selectedCouponId)
    } catch (error) {
      alert.fromError(error).autoDismiss().show()

      throw error
    }

    this.close()
    alert
      .saying('Coupon applied!', `Coupon was applied to ${this.job.name}.`)
      .withState(AlertState.Success)
      .autoDismiss()
      .show()
  })

  fetchUserCoupons = task(async () =>
    this.couponStore.fetchUserCoupons(this.job.owner),
  )

  scrollProvider = new InfiniteScrollProvider({
    limit: 20,
    fetchItems: (params) =>
      this.couponStore.fetchCoupons({
        ...params,
        coupon_type: 'REGULAR',
        search_text: this.searchTerm,
        status: CouponStatus.ACTIVE,
        workspace_id: Id.assert(this.job.workspace.id),
      }),
  })
}
